// MAIN-845/849: surfaces AI menu import jobs on the menu list, linking into
// the real review page for every import that still has items to review.
// MAIN-879: also shows a running import's real progress, and counts what is
// left rather than everything ever imported.
import React from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Button, LinearProgress } from "@mui/material";
import { LIST_MENU_IMPORT_JOBS } from "../../Graphql/queries";
import {
  countImportItems,
  describePendingReview,
  getMenuImportBannerState,
} from "./menuImportBanner.utils";
import { failureCategoryLabel, getImportProgress } from "./menuImportProgress.utils";

// While an import runs, check on it every few seconds; otherwise not at all.
export const IN_PROGRESS_POLL_MS = 3000;

export default function MenuImportBanner({ catererId }) {
  const navigate = useNavigate();
  const { data } = useQuery(LIST_MENU_IMPORT_JOBS, {
    variables: { catererId },
    skip: !catererId,
    fetchPolicy: "cache-and-network",
  });

  // NEEDS_REVIEW is the real terminal state of a successful parse — it is
  // NOT "done"; nothing has been reviewed or published yet. A newer import,
  // failed or not, must not hide an older one that still needs review.
  const { failedJob, inProgressJobs, pendingReviewJobs, latestJobId } = getMenuImportBannerState(
    data?.listMenuImportJobs,
  );

  return (
    <>
      {/*
        MAIN-879: the only poller for import jobs. It runs only while an
        import is queued or processing — the import drawer reads the same
        list from the cache — and stops as soon as none is.
      */}
      {catererId && inProgressJobs.length > 0 && <MenuImportJobsPoller catererId={catererId} />}
      {inProgressJobs.map((job) => {
        const progress = getImportProgress(job);
        return (
          <div key={job._id} className="menu-import-banner" aria-live="polite">
            <div style={{ flex: 1, minWidth: 220 }}>
              <b>Importing your menu: {progress.headline}</b>
              <LinearProgress aria-label="Import in progress" sx={{ mt: 1, mb: 0.5 }} />
              {progress.detail && <div>{progress.detail}</div>}
            </div>
          </div>
        );
      })}
      {failedJob && (
        <div className="menu-import-banner menu-import-banner--error">
          <div>
            <b>Your menu import didn't finish.</b>
            {failureCategoryLabel(failedJob.failureCategory) && (
              <div>{failureCategoryLabel(failedJob.failureCategory)}</div>
            )}
            <div>{failedJob.errorMessage || "Something went wrong reading the file."}</div>
          </div>
        </div>
      )}
      {pendingReviewJobs.map((job) => {
        const { remaining } = countImportItems(job);
        return (
          <div key={job._id} className="menu-import-banner">
            <div>
              <b>Ready for review.</b>
              <div>{describePendingReview(job, latestJobId)}</div>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => navigate(`/menu-import/${job._id}/review`)}>
              Review {remaining} item{remaining === 1 ? "" : "s"}
            </Button>
          </div>
        );
      })}
    </>
  );
}

/**
 * Polls the job list while mounted. Mounted only while an import runs, so
 * polling starts and stops with it; it writes into the same cache entry the
 * banner and the drawer read.
 */
function MenuImportJobsPoller({ catererId }) {
  useQuery(LIST_MENU_IMPORT_JOBS, {
    variables: { catererId },
    fetchPolicy: "network-only",
    pollInterval: IN_PROGRESS_POLL_MS,
    notifyOnNetworkStatusChange: false,
  });
  return null;
}
